import { WhoAmIClue, WhoAmIPlayer } from '../types/games';

export const WHO_AM_I_DEFAULT_HINTS = 7;
export const WHO_AM_I_MIN_HINTS = 7;
export const WHO_AM_I_MAX_HINTS = 10;
export const WHO_AM_I_CLUE_VARIANTS = 4;
export const WHO_AM_I_DEFAULT_MINIMUM_STARTS = 20;
export const WHO_AM_I_EXPORT_MINIMUM_STARTS = 20;
export const WHO_AM_I_AUTOSUGGEST_MIN_CHARACTERS = 3;

export const selectWhoAmIPlayer = (
  players: WhoAmIPlayer[],
  random: () => number = Math.random,
) => {
  const picturedPlayers = players.filter((player) => Boolean(player.HeadshotUrl?.trim()));
  const pool = picturedPlayers.length > 0 ? picturedPlayers : players;
  return pool.length > 0 ? pool[Math.floor(random() * pool.length)] : undefined;
};

export const searchWhoAmIPlayers = (
  players: WhoAmIPlayer[],
  query: string,
  guessedPlayerIds: Set<number>,
) => {
  const normalized = query.trim().toLocaleLowerCase();
  if (normalized.length < WHO_AM_I_AUTOSUGGEST_MIN_CHARACTERS) return [];

  return players
    .filter((player) => !guessedPlayerIds.has(player.PlayerId)
      && player.Name.toLocaleLowerCase().includes(normalized))
    .sort((left, right) => left.Name.localeCompare(right.Name))
    .slice(0, 12);
};

export const buildFallbackWhoAmIClues = (
  player: WhoAmIPlayer,
  hintCount: number = WHO_AM_I_DEFAULT_HINTS,
  clueVariant: number = 0,
): WhoAmIClue[] => {
  const seasons = [...player.Seasons].sort((left, right) => left - right);
  const firstSeason = seasons[0];
  const lastSeason = seasons[seasons.length - 1];
  const primaryFranchise = [...player.Franchises]
    .sort((left, right) => right.GamesStarted - left.GamesStarted)[0];
  const franchiseCount = player.Franchises.length;
  const championshipHint = player.Championships.length > 0
    ? `I finished ${player.Championships.map((championship) => championship.Year).join(', ')} on a Gibsons League championship roster.`
    : `My best archived positional finish was No. ${player.BestSeasonPositionRank} in ${player.BestSeasonPositionRankYear}.`;
  const acquisitionHint = player.LastFantasySeasonWasKept
    ? `I was kept for the ${player.LastFantasySeason} Gibsons League season${player.LastFantasySeasonFranchiseName ? ` by ${player.LastFantasySeasonFranchiseName}` : ''}.`
    : player.LastFantasySeasonDraftRound
      ? `I was drafted in Round ${player.LastFantasySeasonDraftRound} for the ${player.LastFantasySeason} Gibsons League season${player.LastFantasySeasonFranchiseName ? ` by ${player.LastFantasySeasonFranchiseName}` : ''}.`
      : `My most recent archived fantasy starts came in ${player.LastFantasySeason}.`;
  const transactionHint = `Across my Gibsons League career, I was kept ${player.CareerKeeperCount} time${player.CareerKeeperCount === 1 ? '' : 's'}, drafted ${player.CareerDraftCount} time${player.CareerDraftCount === 1 ? '' : 's'}, traded ${player.CareerTradeCount} time${player.CareerTradeCount === 1 ? '' : 's'}, and dropped ${player.CareerDropCount} time${player.CareerDropCount === 1 ? '' : 's'}.`;

  const supportingHints = [
    acquisitionHint,
    `My archive career covers ${seasons.length} season${seasons.length === 1 ? '' : 's'}.`,
    `My archived starts span ${firstSeason} through ${lastSeason}.`,
    championshipHint,
    `My most starts came with ${primaryFranchise?.Name || 'one franchise'}.`,
    `I started for ${franchiseCount} Gibsons League franchise${franchiseCount === 1 ? '' : 's'}.`,
    `My best season average was ${player.PeakSeasonPointsPerGame.toFixed(1)} points per start in ${player.PeakSeasonYear}.`,
    `I averaged ${player.PointsPerGame.toFixed(1)} points across ${player.GamesStarted} archived starts.`,
  ];
  const offset = Math.abs(clueVariant) % supportingHints.length;
  const variedSupportingHints = [...supportingHints.slice(offset), ...supportingHints.slice(0, offset)]
    .slice(0, Math.max(0, hintCount - 2));
  const transactionIndex = Math.min(
    variedSupportingHints.length,
    1 + (Math.abs(clueVariant) % Math.max(1, Math.min(3, variedSupportingHints.length))),
  );
  const variedHints = [...variedSupportingHints];
  variedHints.splice(transactionIndex, 0, transactionHint);
  variedHints.push(`My archive position is ${player.Position}.`);
  return variedHints.slice(0, hintCount).map((hint) => ({ hint, source: 'archive' }));
};
